import { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import youbora from 'youboralib';

let plugin;
function NpawMonitoring({
  adapter = void 0,
  adsAdapter = void 0,
  accountCode,
  isLive = void 0,
  content = void 0,
  userId = void 0,
  profileId = void 0,
  channelName = void 0,
  channelId = void 0,
  userType = void 0,
  playerInfo,
  playSource = void 0,
  chromecastSender = void 0,
  deviceMetadata = void 0,
  vodPlayStartTime = 0,
  msoProvider = void 0,
  error = void 0,
  providerId = void 0,
  subscriberId = void 0
}) {
  const contentTitle = useMemo(() => {
    if (isLive)
      return `LIVE-${channelName || ""}`;
    if (isEpisode(content?.type)) {
      return `VOD-${content?.episode?.showName || ""}-S${content?.episode?.seasonNumber || ""}E${content?.episode?.number || ""}-${content?.title || ""}`;
    }
    return `VOD-${content?.title || ""}`;
  }, [channelName, content, isLive]);
  const getPlugin = useCallback(() => {
    if (!plugin) {
      plugin = new youbora.Plugin({ accountCode });
    }
    return plugin;
  }, [accountCode]);
  useEffect(() => {
    if (adapter) {
      getPlugin().setAdapter(adapter);
    }
  }, [adapter, getPlugin]);
  useEffect(() => {
    if (adsAdapter) {
      getPlugin().setAdsAdapter(adsAdapter);
    }
  }, [adsAdapter, getPlugin]);
  useEffect(() => {
    getPlugin().setOptions({
      // APP
      "app.name": playerInfo.PLAYER_NAME,
      "app.releaseVersion": playerInfo.APP_VERSION?.replace(/-local/i, "")
    });
  }, [getPlugin, playerInfo]);
  useEffect(() => {
    getPlugin().setOptions({
      "device.model": deviceMetadata?.model,
      "device.osVersion": deviceMetadata?.osVersion,
      "content.customDimension.8": deviceMetadata?.firmwareVersion
      // firmwareVersion - Only for HTML5. (kind of OS version, not the browser version). Example: "04.80.10"
    });
  }, [deviceMetadata, getPlugin]);
  useEffect(() => {
    getPlugin().setOptions({
      "content.customDimension.7": chromecastSender
      // chromecastSender - Only for Chromecast. Example: "DTVGo iOS - 4.0.0"
    });
  }, [chromecastSender, getPlugin]);
  useEffect(() => {
    getPlugin().setOptions({
      // USER
      "user.type": userId ? "Registered" : "Unregistered",
      "user.name": userId,
      "content.customDimension.3": userType?.toUpperCase(),
      // userLogin - Origem do assinante. Exemplos: TVE, OTT, SKY-DTH,ALGAR-OTT
      "content.customDimension.11": getValueOrEmpty(msoProvider),
      "content.customDimension.13": profileId || "",
      "content.customDimension.14": getValueOrNotAvailable(subscriberId)
    });
  }, [getPlugin, userId, profileId, userType, subscriberId, msoProvider]);
  useEffect(() => {
    getPlugin().setOptions({
      "content.title": contentTitle,
      "content.isLive": isLive,
      "content.playbackType": isLive ? "LIVE" : "VOD",
      "content.channel": getValueOrEmpty(channelName),
      "content.customDimension.2": getValueOrNotAvailable(providerId),
      // providerId
      "content.customDimension.4": getValueOrEmpty(channelId),
      // channelId
      "content.customDimension.10": isLive ? "" : getValueOrEmpty(vodPlayStartTime)
      // vodPlayStartTime - In seconds
    });
  }, [channelId, channelName, contentTitle, getPlugin, isLive, providerId, vodPlayStartTime]);
  useEffect(() => {
    getPlugin().setOptions({
      "content.customDimension.1": getValueOrEmpty(playSource)
      // playSource - Where the play started, examples: "LiveLander-EPG"/ "HomeLander-FeaturedCarousel"
    });
  }, [getPlugin, playSource]);
  useEffect(() => {
    getPlugin().setOptions({
      // CONTENT
      "content.program": getValueOrNotAvailable(content?.episode?.showName || content?.title),
      "content.id": getValueOrNotAvailable(content?.vrioAssetId),
      "content.season": getOnlyEpisodeValue(content?.episode?.seasonNumber, content?.type),
      "content.episodeTitle": getOnlyEpisodeValue(content?.episode?.seasonNumber, content?.type),
      "content.genre": getValueOrNotAvailable(content?.genres?.get?.(0) || content?.genres?.[0]),
      "content.type": getValueOrNotAvailable(content?.type?.replace(/(LIVE|VOD)_/, "")),
      "content.tvShow": getOnlyEpisodeValue(content?.episode?.showName, content?.type),
      "content.saga": getOnlyEpisodeValue(content?.episode?.showName, content?.type),
      "content.gracenoteId": getValueOrEmpty(content?.metadata?.id),
      // CUSTOM
      "content.customDimension.5": getOnlyEpisodeValue(content?.episode?.number, content?.type),
      // episodeNumber
      "content.customDimension.6": getValueOrNotAvailable(content?.genres?.join(", ")),
      // genreList - This is the full list separated by comma.
      "content.customDimension.9": getValueOrNotAvailable(content?.rating),
      // rating - Example: "L", "18"
      "content.customDimension.15": getValueForInteractivity(content?.interactivities)
    });
  }, [content, getPlugin]);
  useEffect(() => {
    const internalAdapter = getPlugin()?.getAdapter();
    if (error) {
      internalAdapter?.fireFatalError(error.name, error.description, error.metadata, "fatal", "NpawMonitoring");
    }
  }, [error, getPlugin]);
  return null;
}
NpawMonitoring.propTypes = {
  accountCode: PropTypes.string.isRequired,
  adapter: PropTypes.shape({}),
  adsAdapter: PropTypes.shape({}),
  playerInfo: PropTypes.shape({
    PLAYER_NAME: PropTypes.string.isRequired,
    APP_VERSION: PropTypes.string.isRequired
  }).isRequired,
  content: PropTypes.shape({
    title: PropTypes.string,
    vrioAssetId: PropTypes.string,
    genres: PropTypes.shape([]),
    type: PropTypes.string,
    rating: PropTypes.string,
    episode: PropTypes.shape({
      showName: PropTypes.string,
      seasonNumber: PropTypes.number,
      number: PropTypes.number
    }),
    metadata: PropTypes.shape({
      id: PropTypes.string
    })
  }),
  userId: PropTypes.string,
  profileId: PropTypes.string,
  userType: PropTypes.string,
  playSource: PropTypes.string,
  deviceMetadata: PropTypes.shape({
    model: PropTypes.string,
    osVersion: PropTypes.string,
    firmwareVersion: PropTypes.string
  }),
  chromecastSender: PropTypes.string,
  vodPlayStartTime: PropTypes.number,
  channelName: PropTypes.string,
  channelId: PropTypes.string,
  isLive: PropTypes.bool,
  msoProvider: PropTypes.string,
  error: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    metadata: PropTypes.shape()
  }),
  providerId: PropTypes.string,
  subscriberId: PropTypes.string
};
const NOT_AVAILABLE = "Not available";
const NOT_APPLICABLE = "Not applicable";
function isEpisode(contentType) {
  return contentType?.includes("EPISODE");
}
function getValueOrEmpty(value) {
  return value || "";
}
function getValueOrNotAvailable(value) {
  return value || NOT_AVAILABLE;
}
function getOnlyEpisodeValue(value, contentType) {
  if (!contentType)
    return NOT_AVAILABLE;
  return isEpisode(contentType) ? value : NOT_APPLICABLE;
}
function getValueForInteractivity(interactivitiesValueObj) {
  if (!interactivitiesValueObj)
    return NOT_AVAILABLE;
  return Object.keys(
    interactivitiesValueObj.toJSON ? interactivitiesValueObj.toJSON() : interactivitiesValueObj
  ).map((key) => {
    const value = interactivitiesValueObj[key];
    if (!value)
      return "";
    return key.charAt(0).toUpperCase() + key.slice(1);
  }).filter((val) => !!val).join(",");
}

export { NpawMonitoring as default };
//# sourceMappingURL=NpawMonitoring.js.map
